import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Stack,
  Divider,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import axios from 'axios';
import { displayDateTimeToUTC } from '../../../../utils/timezoneUtils';
import { getDutyWorkLocationDisplayCode } from '../utils/crewDutyRosterUtils';
import { CREW_CLEARANCE_CONFIG, formatCrewClearanceYesNo } from '../../../../utils/crewClearance';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || '/api';

const MAX_ROSTER_REPEAT_CYCLES = 52;

/**
 * Create a multi-day FLIGHT (rosterPlanOnly) block plus SCHEDULED_DUTY_OFF in display timezone.
 * Optional: repeat the same work length + off length back-to-back for multiple cycles.
 */
const RosteringPlanDialog = ({
  open,
  onClose,
  crewMembers = [],
  displayTimezone = 'Asia/Kolkata',
  defaultPlannedOffDays = 10,
  onCreated
}) => {
  const [pilot, setPilot] = useState(null);
  const [workStart, setWorkStart] = useState('');
  const [workEnd, setWorkEnd] = useState('');
  const [offDaysCount, setOffDaysCount] = useState(defaultPlannedOffDays);
  const [remarks, setRemarks] = useState('');
  const [repeatSchedule, setRepeatSchedule] = useState(false);
  const [additionalCycles, setAdditionalCycles] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [workLocationCity, setWorkLocationCity] = useState('');
  const [workLocationCode, setWorkLocationCode] = useState('');

  useEffect(() => {
    if (open) {
      setPilot(null);
      setWorkStart('');
      setWorkEnd('');
      setOffDaysCount(defaultPlannedOffDays);
      setRemarks('');
      setRepeatSchedule(false);
      setAdditionalCycles(1);
      setWorkLocationCity('');
      setWorkLocationCode('');
      setError('');
    }
  }, [open, defaultPlannedOffDays]);

  useEffect(() => {
    if (pilot?._id) {
      setWorkLocationCity(pilot.workLocation?.city || '');
      setWorkLocationCode('');
    }
  }, [pilot?._id]);

  const baseCityOptions = useMemo(
    () =>
      [...new Set(
        (crewMembers || [])
          .map((c) => String(c?.workLocation?.city || '').trim())
          .filter(Boolean)
      )].sort((a, b) => a.localeCompare(b)),
    [crewMembers]
  );

  const workDayCount = useMemo(() => {
    if (!workStart || !workEnd) return 0;
    const a = dayjs(workStart);
    const b = dayjs(workEnd);
    if (!a.isValid() || !b.isValid() || b.isBefore(a)) return 0;
    return b.diff(a, 'day') + 1;
  }, [workStart, workEnd]);

  /** Repeat: 1 + additionalCycles, capped (max 52 work+off pairs). */
  const totalCycles = useMemo(() => {
    if (!repeatSchedule) return 1;
    const add = Math.min(MAX_ROSTER_REPEAT_CYCLES - 1, Math.max(1, Number(additionalCycles) || 1));
    return 1 + add;
  }, [repeatSchedule, additionalCycles]);

  const preview = useMemo(() => {
    if (!workStart || !workEnd || offDaysCount < 1 || workDayCount < 1) return null;
    let wEnd = workEnd;
    let lastOffOverall = '';
    for (let c = 0; c < totalCycles; c++) {
      const firstOff = dayjs(wEnd).add(1, 'day').format('YYYY-MM-DD');
      lastOffOverall = dayjs(firstOff).add(offDaysCount - 1, 'day').format('YYYY-MM-DD');
      if (c < totalCycles - 1) {
        const nextWs = dayjs(lastOffOverall).add(1, 'day').format('YYYY-MM-DD');
        wEnd = dayjs(nextWs).add(workDayCount - 1, 'day').format('YYYY-MM-DD');
      }
    }
    const fo1 = dayjs(workEnd).add(1, 'day').format('YYYY-MM-DD');
    const lo1 = dayjs(fo1).add(offDaysCount - 1, 'day').format('YYYY-MM-DD');
    return {
      cycles: totalCycles,
      firstOff: fo1,
      lastOffFirstCycle: lo1,
      lastOffOverall
    };
  }, [workEnd, workDayCount, offDaysCount, totalCycles]);

  const handleSubmit = async () => {
    setError('');
    if (!pilot?._id) {
      setError('Select a pilot.');
      return;
    }
    if (!workStart || !workEnd) {
      setError('Work start and end dates are required.');
      return;
    }
    if (workDayCount < 1) {
      setError('Work end date must be on or after work start date.');
      return;
    }
    if (offDaysCount < 1) {
      setError('Off days count must be at least 1.');
      return;
    }
    const cityTrim = workLocationCity.trim();
    if (!cityTrim) {
      setError('Work-Duty Location (city) is required. It defaults to the pilot’s base city when you select a pilot.');
      return;
    }

    const tz = displayTimezone || 'UTC';
    const cycles = totalCycles;
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    const codeNorm = workLocationCode.trim().toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);
    const flightLocationPayload = {
      dutyWorkLocationCity: cityTrim,
      ...(codeNorm ? { dutyWorkLocationCode: codeNorm } : {})
    };

    const basePayload = {
      crewId: pilot._id,
      aircraftType: 'Helicopter',
      remarks: remarks || undefined
    };

    let cycleWorkStart = workStart;
    let cycleWorkEnd = workEnd;

    setSubmitting(true);
    try {
      for (let c = 0; c < cycles; c++) {
        const workStartIso = displayDateTimeToUTC(cycleWorkStart, '00:00', tz);
        const workEndIso = displayDateTimeToUTC(cycleWorkEnd, '23:59', tz);
        if (!workStartIso || !workEndIso) {
          setError(`Invalid work dates for cycle ${c + 1} in the selected timezone.`);
          return;
        }

        await axios.post(
          `${API_URL}/ops/crew-duty-roster`,
          {
            ...basePayload,
            ...flightLocationPayload,
            dutyType: 'FLIGHT',
            rosterPlanOnly: true,
            rosterPlan: 'plan_only',
            dutyStartTime: workStartIso,
            dutyEndTime: workEndIso
          },
          { headers }
        );

        const firstOff = dayjs(cycleWorkEnd).add(1, 'day').format('YYYY-MM-DD');
        const lastOff = dayjs(firstOff).add(offDaysCount - 1, 'day').format('YYYY-MM-DD');
        const offStartIso = displayDateTimeToUTC(firstOff, '00:00', tz);
        const offEndIso = displayDateTimeToUTC(lastOff, '23:59', tz);
        if (!offStartIso || !offEndIso) {
          setError(`Could not compute off block for cycle ${c + 1} in timezone.`);
          return;
        }

        await axios.post(
          `${API_URL}/ops/crew-duty-roster`,
          {
            ...basePayload,
            dutyType: 'SCHEDULED_DUTY_OFF',
            rosterPlan: 'plan_only',
            dutyStartTime: offStartIso,
            dutyEndTime: offEndIso
          },
          { headers }
        );

        if (c < cycles - 1) {
          const nextStart = dayjs(lastOff).add(1, 'day').format('YYYY-MM-DD');
          cycleWorkStart = nextStart;
          cycleWorkEnd = dayjs(nextStart).add(workDayCount - 1, 'day').format('YYYY-MM-DD');
        }
      }

      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create roster plan.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const genderLabel = (g) => {
    if (g === 'MALE') return 'Male';
    if (g === 'FEMALE') return 'Female';
    if (g === 'OTHER') return 'Other';
    return '—';
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create duty roster</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Autocomplete
            options={crewMembers}
            getOptionLabel={(o) => (o ? `${o.firstName || ''} ${o.lastName || ''}`.trim() : '')}
            value={pilot}
            onChange={(_, v) => setPilot(v)}
            renderInput={(params) => <TextField {...params} label="Pilot" required />}
          />
          {pilot && (
            <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
              {CREW_CLEARANCE_CONFIG.map(({ field, label }) => (
                <Typography key={field} variant="caption" component="div">
                  {label}: {formatCrewClearanceYesNo(pilot[field])}
                </Typography>
              ))}
              <Typography variant="caption" component="div">
                Base (city): {pilot.workLocation?.city || '—'}
              </Typography>
              <Typography variant="caption" component="div">
                Gender: {genderLabel(pilot.gender)}
              </Typography>
            </Box>
          )}
          <Divider />
          <Autocomplete
            freeSolo
            options={baseCityOptions}
            value={workLocationCity}
            onChange={(_, v) => setWorkLocationCity(v || '')}
            onInputChange={(_, v) => setWorkLocationCity(v || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Work-Duty Location (city) / Base"
                required
                helperText="Where the pilot is assigned to work for this plan. Defaults to base city; pick or type another city."
              />
            )}
          />
          <TextField
            label="City short code (4 letters)"
            value={workLocationCode}
            onChange={(e) => setWorkLocationCode(e.target.value.toUpperCase().replace(/[^A-Za-z]/g, '').slice(0, 4))}
            fullWidth
            inputProps={{ maxLength: 4 }}
            placeholder="e.g. BENG"
            helperText={
              workLocationCity.trim()
                ? `Shown on roster with location code ${
                    getDutyWorkLocationDisplayCode(workLocationCity, workLocationCode) || '—'
                  }. Leave blank to use the first 4 letters of the city (e.g. Bengaluru → Beng).`
                : 'Optional. If empty, the first 4 letters of the city are used.'
            }
          />
          <TextField
            label="Work start date"
            type="date"
            value={workStart}
            onChange={(e) => setWorkStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />
          <TextField
            label="Work end date (inclusive)"
            type="date"
            value={workEnd}
            onChange={(e) => setWorkEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
            helperText={workDayCount > 0 ? `${workDayCount} calendar day(s) in work block` : ' '}
          />
          <TextField
            label="Off days (after work block)"
            type="number"
            value={offDaysCount}
            onChange={(e) => setOffDaysCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
            inputProps={{ min: 1, max: 365 }}
            fullWidth
            required
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={repeatSchedule}
                onChange={(e) => setRepeatSchedule(e.target.checked)}
              />
            }
            label="Repeat this schedule (same work length and off length, back-to-back)"
          />
          {repeatSchedule && (
            <TextField
              label="Additional full cycles"
              type="number"
              value={additionalCycles}
              onChange={(e) =>
                setAdditionalCycles(Math.min(51, Math.max(1, parseInt(e.target.value, 10) || 1)))
              }
              inputProps={{ min: 1, max: 51 }}
              fullWidth
              helperText={`Total work+off sequences: ${totalCycles} (max ${MAX_ROSTER_REPEAT_CYCLES}). Each new work block starts the day after the previous off block ends.`}
            />
          )}
          <TextField
            label="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            helperText="Applied to every flight (plan) and scheduled off segment in this plan, including repeated cycles."
          />
          {preview && workDayCount > 0 && (
            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                Preview
              </Typography>
              <Typography variant="body2">
                Cycle 1 — Flight (plan)
                {workLocationCity.trim()
                  ? ` · ${getDutyWorkLocationDisplayCode(workLocationCity, workLocationCode)}`
                  : ''}
                : {workStart} → {workEnd} ({workDayCount} day{workDayCount !== 1 ? 's' : ''})
              </Typography>
              <Typography variant="body2">
                Cycle 1 — Off: {preview.firstOff} → {preview.lastOffFirstCycle} ({offDaysCount} day
                {offDaysCount !== 1 ? 's' : ''})
              </Typography>
              {preview.cycles > 1 && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Then {preview.cycles - 1} more identical work+off cycle{preview.cycles - 1 !== 1 ? 's' : ''} through{' '}
                  <strong>{preview.lastOffOverall}</strong> (last off day).
                </Typography>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RosteringPlanDialog;
